import apiClient, { clearSharedGetCache } from "../api/client";
import { STORES, localDb } from "./localDb";

const LONG_POLL_WAIT_SECONDS = 20;
const LONG_POLL_TIMEOUT_MS = 45 * 1000;
const SNAPSHOT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const LOCAL_FIRST_STORAGE_KEY = "lumen-tms-local-first-mode";
const META_KEYS = { state: "state", lookups: "lookups", settings: "settings" };
const FULL_REFRESH_ENTITIES = ["board", "lookups", "settings", "load", "driver", "truck", "trailer", "customer", "location", "invoice", "planning", "repeat_template"];

const byText = (pick) => (left, right) => String(pick(left) || "").localeCompare(String(pick(right) || ""), undefined, { sensitivity: "base" });

const MASTER_LOOKUP_LISTS = {
  driver: { list: "drivers", sort: byText((item) => `${item.first_name || ""} ${item.last_name || ""}`) },
  truck: { list: "trucks", sort: byText((item) => item.truck_number) },
  trailer: { list: "trailers", sort: byText((item) => item.trailer_number) },
  customer: { list: "customers", sort: byText((item) => item.customer_name) },
  location: { list: "locations", sort: byText((item) => item.company_name) },
};

const isBrowserOnline = () => (typeof navigator === "undefined" ? true : navigator.onLine !== false);
const isNetworkError = (error) => !error?.response;
const isCanceled = (error) => error?.code === "ERR_CANCELED" || error?.name === "CanceledError";
const nowIso = () => new Date().toISOString();

class SyncEngine {
  constructor() {
    this.listeners = new Set();
    this.boardRows = new Map();
    this.lookups = null;
    this.settings = null;
    this.outbox = [];
    this.presence = [];
    this.presenceTs = 0;
    this.currentUser = null;
    this.openConflicts = 0;
    this.editingTarget = null;
    this.presenceTimer = null;
    this.cursor = 0;
    this.snapshotAt = 0;
    this.running = false;
    this.abortController = null;
    this.wakeResolver = null;
    this.status = { state: "idle", online: isBrowserOnline(), ready: false, lastSyncAt: null, lastError: "", pendingCount: 0, cursor: 0, localFirst: this.isLocalFirstEnabled(), boardRowCount: 0, openConflicts: 0, presenceVersion: 0 };
    this.handleOnline = () => { this.setStatus({ online: true }); this.wake(); };
    this.handleOffline = () => this.setStatus({ online: false, state: "offline" });
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(event) {
    this.listeners.forEach((listener) => {
      try { listener(event); } catch { /* listener errors must not break sync */ }
    });
  }

  setStatus(patch) {
    this.status = { ...this.status, ...patch, pendingCount: this.outbox.length, localFirst: this.isLocalFirstEnabled(), boardRowCount: this.boardRows.size, openConflicts: this.openConflicts, presenceVersion: this.presenceTs };
    this.emit({ type: "status", status: this.status });
  }

  getStatus() { return this.status; }

  isLocalFirstEnabled() {
    try { return window.localStorage.getItem(LOCAL_FIRST_STORAGE_KEY) !== "off"; } catch { return true; }
  }

  setLocalFirstEnabled(enabled) {
    window.localStorage.setItem(LOCAL_FIRST_STORAGE_KEY, enabled ? "on" : "off");
    clearSharedGetCache();
    this.setStatus({});
    this.emit({ type: "changed", entities: new Set(FULL_REFRESH_ENTITIES) });
  }

  getBoardRows() { return Array.from(this.boardRows.values()); }
  getLookups() { return this.isLocalFirstEnabled() && this.status.ready ? this.lookups : null; }
  getSettings() { return this.isLocalFirstEnabled() && this.status.ready ? this.settings : null; }
  getOutbox() { return [...this.outbox]; }
  getCurrentUser() { return this.currentUser; }
  setCurrentUser(user) { this.currentUser = user ? { id: user.id, name: user.name || user.full_name || user.email } : null; }
  isOwnChange(change) { return Boolean(change?.changed_by?.id && this.currentUser?.id && change.changed_by.id === this.currentUser.id); }
  getEditors(entityType, entityId) {
    const entry = this.presence.find((item) => item.entity_type === entityType && item.entity_id === entityId);
    return (entry?.users || []).filter((user) => user.id !== this.currentUser?.id);
  }
  getPendingEntityIds() { return new Set(this.outbox.map((item) => item.entityId).filter(Boolean)); }

  async start() {
    if (this.running) {
      return;
    }
    this.running = true;
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
    await this.restoreFromLocal();
    this.loop();
  }

  stop() {
    this.running = false;
    this.clearEditing();
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
    this.wake();
    this.setStatus({ state: "idle" });
  }

  async clearLocalData() {
    try { await localDb.clearAll(); } catch { /* no-op */ }
    this.boardRows = new Map();
    this.lookups = null;
    this.settings = null;
    this.outbox = [];
    this.cursor = 0;
    this.snapshotAt = 0;
    this.setStatus({ ready: false, cursor: 0, lastSyncAt: null, lastError: "" });
  }

  async restoreFromLocal() {
    try {
      const [state, lookups, settings, rows, outbox] = await Promise.all([
        localDb.get(STORES.meta, META_KEYS.state),
        localDb.get(STORES.meta, META_KEYS.lookups),
        localDb.get(STORES.meta, META_KEYS.settings),
        localDb.getAll(STORES.boardRows),
        localDb.getAll(STORES.outbox),
      ]);
      this.cursor = state?.cursor || 0;
      this.snapshotAt = state?.snapshotAt || 0;
      this.lookups = lookups?.value || null;
      this.settings = settings?.value || null;
      this.boardRows = new Map((rows || []).map((row) => [row.id, row]));
      this.outbox = outbox || [];
      const ready = Boolean(this.cursor && this.lookups);
      this.setStatus({ ready, cursor: this.cursor, lastSyncAt: state?.lastSyncAt || null, state: ready ? "syncing" : "bootstrapping" });
      if (ready) {
        this.emit({ type: "changed", entities: new Set(["board", "lookups", "settings"]) });
      }
    } catch {
      this.setStatus({ ready: false, state: "bootstrapping" });
    }
  }

  wake() {
    if (this.wakeResolver) {
      this.wakeResolver();
      this.wakeResolver = null;
    }
    this.abortController?.abort();
  }

  waitForWake(ms) {
    return new Promise((resolve) => {
      this.wakeResolver = resolve;
      window.setTimeout(resolve, ms);
    });
  }

  newSignal() {
    this.abortController = new AbortController();
    return this.abortController.signal;
  }

  async loop() {
    let backoffMs = 1000;
    while (this.running) {
      if (!isBrowserOnline()) {
        this.setStatus({ online: false, state: "offline" });
        await this.waitForWake(15000);
        continue;
      }
      try {
        await this.replayOutbox();
        const needsSnapshot = !this.cursor || !this.lookups || Date.now() - this.snapshotAt > SNAPSHOT_MAX_AGE_MS;
        if (needsSnapshot) {
          await this.fullSnapshot();
        } else {
          await this.pull(LONG_POLL_WAIT_SECONDS);
        }
        backoffMs = 1000;
      } catch (error) {
        if (!this.running) {
          break;
        }
        if (isCanceled(error)) {
          continue;
        }
        const offline = isNetworkError(error) || !isBrowserOnline();
        const authFailure = [401, 403].includes(error?.response?.status);
        this.setStatus({
          state: offline ? "offline" : "error",
          online: isBrowserOnline() && !offline,
          lastError: offline ? "Connection lost — reconnecting automatically." : error?.response?.data?.detail || error?.message || "Sync failed.",
        });
        await this.waitForWake(authFailure ? 30000 : backoffMs);
        backoffMs = Math.min(backoffMs * 2, 30000);
      }
    }
  }

  async fullSnapshot() {
    this.setStatus({ state: "syncing" });
    const { data } = await apiClient.get("/sync/snapshot", { signal: this.newSignal(), timeout: LONG_POLL_TIMEOUT_MS });
    this.boardRows = new Map((data.board_rows || []).map((row) => [row.id, row]));
    this.lookups = data.lookups;
    this.settings = data.settings;
    this.cursor = data.cursor;
    this.snapshotAt = Date.now();
    this.applyPresence(data);
    this.openConflicts = data.open_conflicts || 0;
    if (data.current_user) {
      this.currentUser = data.current_user;
    }
    await Promise.all([
      localDb.replaceAll(STORES.boardRows, data.board_rows || []),
      localDb.put(STORES.meta, { key: META_KEYS.lookups, value: data.lookups }),
      localDb.put(STORES.meta, { key: META_KEYS.settings, value: data.settings }),
    ]);
    await this.persistState();
    clearSharedGetCache();
    this.setStatus({ state: "live", online: true, ready: true, cursor: this.cursor, lastSyncAt: nowIso(), lastError: "" });
    this.emit({ type: "changed", entities: new Set(FULL_REFRESH_ENTITIES), full: true });
  }

  async pull(waitSeconds) {
    const { data } = await apiClient.get("/sync/changes", { params: { since: this.cursor, wait: waitSeconds, presence_since: this.presenceTs }, signal: this.newSignal(), timeout: LONG_POLL_TIMEOUT_MS });
    if (data.reset_required || data.changes?.some((change) => change.entity_type === "full_refresh")) {
      await this.fullSnapshot();
      return;
    }
    this.applyPresence(data);
    if (data.changes?.length) {
      await this.applyChanges(data.changes);
    }
    this.cursor = data.cursor;
    await this.persistState();
    this.setStatus({ state: "live", online: true, ready: true, cursor: this.cursor, lastSyncAt: nowIso(), lastError: "" });
    if (data.has_more) {
      await this.pull(0);
    }
  }

  async persistState() {
    await localDb.put(STORES.meta, { key: META_KEYS.state, cursor: this.cursor, snapshotAt: this.snapshotAt, lastSyncAt: nowIso() });
  }

  async applyChanges(changes) {
    const entities = new Set();
    const loadChanges = [];
    let lookupsTouched = false;
    changes.forEach((change) => {
      entities.add(change.entity_type);
      if (change.entity_type === "load") {
        const trips = change.data?.trips || [];
        loadChanges.push({ loadId: change.entity_id, trips });
        Array.from(this.boardRows.values()).filter((row) => row.load_id === change.entity_id).forEach((row) => this.boardRows.delete(row.id));
        trips.forEach((trip) => this.boardRows.set(trip.id, trip));
        entities.add("board");
      } else if (MASTER_LOOKUP_LISTS[change.entity_type]) {
        this.patchLookupList(change);
        lookupsTouched = true;
      } else if (change.entity_type === "conflict") {
        this.refreshConflictCount();
      } else if (change.entity_type === "settings" && change.data) {
        this.settings = change.data;
        if (this.lookups) {
          this.lookups = { ...this.lookups, distance_unit: change.data.distance_unit };
        }
        lookupsTouched = true;
      }
    });
    if (loadChanges.length) {
      await localDb.applyBoardLoadChanges(loadChanges);
    }
    if (lookupsTouched) {
      entities.add("lookups");
      await Promise.all([
        localDb.put(STORES.meta, { key: META_KEYS.lookups, value: this.lookups }),
        localDb.put(STORES.meta, { key: META_KEYS.settings, value: this.settings }),
      ]);
    }
    clearSharedGetCache();
    this.emit({ type: "changed", entities, changes });
  }

  patchLookupList(change) {
    if (!this.lookups) {
      return;
    }
    const config = MASTER_LOOKUP_LISTS[change.entity_type];
    const list = (this.lookups[config.list] || []).filter((item) => item.id !== change.entity_id);
    if (change.action === "upsert" && change.data) {
      list.push(change.data);
    }
    list.sort(config.sort);
    this.lookups = { ...this.lookups, [config.list]: list };
  }

  applyPresence(data) {
    if (!data || !Array.isArray(data.presence)) {
      return;
    }
    const changed = JSON.stringify(data.presence) !== JSON.stringify(this.presence);
    this.presence = data.presence;
    this.presenceTs = data.presence_ts || this.presenceTs;
    if (changed) {
      this.emit({ type: "presence", presence: this.presence });
    }
  }

  async sendPresence(target, editing) {
    try {
      const { data } = await apiClient.post("/sync/presence", { entity_type: target.entityType, entity_id: target.entityId, editing });
      this.applyPresence(data);
    } catch { /* presence is best-effort */ }
  }

  setEditing(entityType, entityId) {
    const same = this.editingTarget && this.editingTarget.entityType === entityType && this.editingTarget.entityId === entityId;
    if (same) {
      return;
    }
    this.clearEditing();
    if (!entityType || !entityId) {
      return;
    }
    this.editingTarget = { entityType, entityId };
    this.sendPresence(this.editingTarget, true);
    this.presenceTimer = window.setInterval(() => this.editingTarget && this.sendPresence(this.editingTarget, true), 20000);
  }

  clearEditing() {
    if (this.presenceTimer) {
      window.clearInterval(this.presenceTimer);
      this.presenceTimer = null;
    }
    if (this.editingTarget) {
      const target = this.editingTarget;
      this.editingTarget = null;
      this.sendPresence(target, false);
    }
  }

  async refreshConflictCount() {
    try {
      const { data } = await apiClient.get("/sync/conflicts", { params: { status: "open", limit: 1 } });
      this.openConflicts = data.open_count || 0;
      this.setStatus({});
      this.emit({ type: "conflicts" });
    } catch { /* best-effort */ }
  }

  async fetchConflicts(status = "open") {
    const { data } = await apiClient.get("/sync/conflicts", { params: { status, limit: 50 } });
    this.openConflicts = data.open_count || 0;
    this.setStatus({});
    return data.items || [];
  }

  async resolveConflict(conflictId) {
    const { data } = await apiClient.post(`/sync/conflicts/${conflictId}/resolve`);
    this.openConflicts = Math.max(0, this.openConflicts - 1);
    this.setStatus({});
    this.emit({ type: "conflicts" });
    return data;
  }

  async write({ method, url, data, label, entityType, entityId, headers }) {
    const outboxItem = { method, url, data, label, entityType, entityId, headers };
    if (!isBrowserOnline()) {
      return this.enqueue(outboxItem);
    }
    try {
      const response = await apiClient.request({ method, url, data, headers });
      clearSharedGetCache();
      return { queued: false, data: response.data };
    } catch (error) {
      if (isNetworkError(error) && !isCanceled(error)) {
        return this.enqueue(outboxItem);
      }
      throw error;
    }
  }

  async enqueue(item) {
    const record = { ...item, createdAt: nowIso(), failed: false, error: "" };
    const id = await localDb.add(STORES.outbox, record);
    record.id = id;
    this.outbox.push(record);
    this.setStatus({});
    this.emit({ type: "outbox" });
    this.wake();
    return { queued: true };
  }

  async replayOutbox() {
    if (!this.outbox.length) {
      return;
    }
    for (const item of [...this.outbox]) {
      if (item.failed) {
        continue;
      }
      try {
        await apiClient.request({ method: item.method, url: item.url, data: item.data, headers: item.headers });
        await this.removeOutboxItem(item.id);
      } catch (error) {
        if (isNetworkError(error)) {
          throw error;
        }
        item.failed = true;
        item.error = error?.response?.data?.detail || error?.message || "Server rejected this change.";
        await localDb.put(STORES.outbox, item);
        this.setStatus({});
        this.emit({ type: "outbox" });
      }
    }
    clearSharedGetCache();
  }

  async retryOutboxItem(id) {
    const item = this.outbox.find((entry) => entry.id === id);
    if (!item) {
      return;
    }
    item.failed = false;
    item.error = "";
    await localDb.put(STORES.outbox, item);
    this.setStatus({});
    this.emit({ type: "outbox" });
    this.wake();
  }

  async discardOutboxItem(id) {
    await this.removeOutboxItem(id);
  }

  async removeOutboxItem(id) {
    await localDb.delete(STORES.outbox, id);
    this.outbox = this.outbox.filter((entry) => entry.id !== id);
    this.setStatus({});
    this.emit({ type: "outbox" });
  }

  syncNow() { this.wake(); }

  refreshAll() {
    this.snapshotAt = 0;
    this.wake();
  }
}

export const syncEngine = new SyncEngine();

if (typeof window !== "undefined") {
  window.__lumenSyncEngine = syncEngine;
}
